import { Chess, Color, Piece, PieceSymbol } from "chess.js";
import { Attempt, AttemptType, EndReason, Evaluation, Judgement, Level, PlayerColor, Settings, State } from "./models";
import { fetchLichessLink } from "./fetchLichessLink";
import { OtAttempt2 } from "./PositionStats";

export const isInPlayingState = (state: State) => state === "playing" || state === "attempt_finished";

export const getAttemptType = (playerColor: PlayerColor, ev: number): AttemptType => {
    if (playerColor === "w") {
        if (ev < -0.5) {
            return "bad";
        } else if (ev < 0) {
            return "okay";
        } else if (ev < 0.8) {
            return "good";
        } else {
            return "brilliant";
        }
    }

    if (playerColor === "b") {
        if (ev > 0.9) {
            return "bad";
        } else if (ev > 0.5) {
            return "okay";
        } else if (ev > -0.5) {
            return "good";
        } else {
            return "brilliant";
        }
    }

    return "unknown";
};

export const getPlayerColorText = (playerColor?: PlayerColor) => {
    if (!playerColor) {
        return "";
    }
    return playerColor === "w" ? "White" : "Black";
};

export const getCanWinTrophies = (settings: Settings) => {
    return !settings.preferDubious;
};

export const getEngineLevelForPosition = (positionRating: number): Level => {
    switch (true) {
        case positionRating < 1000:
            return "400";
        case positionRating < 1200:
            return "1000";
        case positionRating < 1400:
            return "1200";
        case positionRating < 1600:
            return "1400";
        case positionRating < 1800:
            return "1600";
        case positionRating < 2000:
            return "1800";
        case positionRating < 2200:
            return "2000";
        case positionRating < 2500:
            return "2200";
        case positionRating < 2600:
            return "2500";
        case positionRating >= 2600:
            return "Masters";
        default:
            return "1400";
    }
};

export const analyzeAttemptOnLichces = async (attempt: Attempt | OtAttempt2, isMobile) => {
    const oldGame = new Chess();
    oldGame.loadPgn(attempt.pgn);
    const gameLink = await fetchLichessLink(oldGame, attempt.playerColor || "w", undefined, isMobile);

    // @ts-expect-error err
    window.open(gameLink).focus();
};

export const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const getLengthOfPgn = (pgn?: string) => {
    if (!pgn) {
        return 999;
    }
    const game = new Chess();
    game.loadPgn(pgn);
    return game.history().length;
};

// Copied from Endgametrainer
export const getKFactor = (numberOfAttempts: number) => {
    if (numberOfAttempts <= 5) {
        return 128;
    }
    if (numberOfAttempts <= 10) {
        return 128;
    }
    if (numberOfAttempts <= 20) {
        return 96;
    }
    return 64;
};

// Copied from Endgametrainer
export const getRatingGain = (
    rating: number,
    engineLevel: number,
    evaluation: number,
    playerColor: "w" | "b",
    k = 64,
    endReason: EndReason | null,
) => {
    // Making it nicer for user and more fair when unique position
    // if (evaluation !== 0.3 && playerColor === "w" && endReason === "uniquePosition") {
    //     evaluation += 0.1;
    // }
    // if (evaluation !== 0.3 && playerColor === "b" && endReason === "uniquePosition") {
    //     evaluation -= 0.1;
    // }

    const INITIAL_EVAL = EQUALIBRIUM;
    let ratingGain = Math.min((k / 2) * Math.abs(evaluation - INITIAL_EVAL) ** 0.5, k);

    if (playerColor === "w" && evaluation < INITIAL_EVAL) {
        ratingGain *= -1;
    }
    if (playerColor === "b" && evaluation > INITIAL_EVAL) {
        ratingGain *= -1;
    }

    const STANDARD_DEV = 1600; // Normally 400
    const expectedScore = 1 / (1 + 10 ** ((rating - engineLevel) / STANDARD_DEV));
    const result = Math.round(ratingGain * expectedScore);
    return result;
};

export const EQUALIBRIUM = 0.25;
export const MISTAKE_DELTA = 0.35;

export const getOpponentLevelToDisplay = (level: Level) => {
    if (level === "400") {
        return "600-1000";
    }
    if (level === "1000") {
        return "1000-1200";
    }
    if (level === "1200") {
        return "1200-1400";
    }
    if (level === "1400") {
        return "1400-1600";
    }
    if (level === "1600") {
        return "1600-1800";
    }
    if (level === "1800") {
        return "1800-2000";
    }
    if (level === "2000") {
        return "2000-2200";
    }
    if (level === "2200") {
        return "2200-2500";
    }
    if (level === "2500") {
        return "2500-2600";
    }
    if (level === "Masters") {
        return "Masters";
    }

    return "Masters";
};

export const INITIAL_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";

export const INITIAL_POSITION = {
    name: "Initial position",
    originalName: "Initial position",
    fen: INITIAL_FEN,
};

export const formatEvaluation = (evaluation?: string | number): string => {
    if (!evaluation) {
        return "-";
    }

    evaluation = typeof evaluation === "string" ? parseFloat(evaluation) : evaluation;

    evaluation = evaluation / -1;

    if (Math.abs(evaluation) <= 10) {
        evaluation /= 10;
        return evaluation >= 0 ? `+${evaluation.toFixed(2)}` : evaluation.toFixed(2);
    }

    // Convert from centipawns to decimal
    const decimal = evaluation / 100;
    return decimal >= 0 ? `+${decimal.toFixed(2)}` : decimal.toFixed(2);
};

// https://github.com/lichess-org/lila/pull/11148
// https://lichess.org/page/accuracy
export const getWinningChance = (cp: number, playerColor: PlayerColor) => {
    const winningChance = 50 + 50 * (2 / (1 + Math.exp(-0.00368208 * cp)) - 1);
    // return winningChance;
    return playerColor === "w" ? winningChance : 100 - winningChance;
};

export const getAccuracy = (evalBefore: number, evalAfter: number, playerColor: PlayerColor) => {
    const winPercentBefore = getWinningChance(evalBefore * 100, playerColor);
    const winPercentAfter = getWinningChance(evalAfter * 100, playerColor);
    const accuracy = 103.1668 * Math.exp(-0.04354 * (winPercentBefore - winPercentAfter)) - 3.1669;
    return accuracy;
};

// Check if move is inaccuracy, mistake or blunder the same way Lichess does it
// https://github.com/lichess-org/lila/blob/cf9e10df24b767b3bc5ee3d88c45437ac722025d/modules/analyse/src/main/Advice.scala#L52
// Inacurracy: At least 5 % change in winning chance
// Mistake: At least 10 % change in winning chance
// Blunder: At least 15 % change in winning chance
export const getLichessJugdement = (
    evalBefore: number,
    evalAfter: number,
    playerColor: PlayerColor,
): Judgement | undefined => {
    const winningChanceBefore = getWinningChance(evalBefore * 100, playerColor);
    const winningChanceAfter = getWinningChance(evalAfter * 100, playerColor);

    let delta = winningChanceBefore - winningChanceAfter;
    // let delta = Math.abs(winningChanceBefore - winningChanceAfter);

    if (delta >= 15) {
        return "blunder";
    }

    if (delta >= 10) {
        return "mistake";
    }

    if (delta >= 5) {
        return "inaccuracy";
    }

    return undefined;
};

export const setJudementsForEvaluations = (evaluations: Record<number, Evaluation>, playerColor: PlayerColor) => {
    const sortedKeys = Object.keys(evaluations)
        .map(Number)
        .sort((a, b) => a - b);
    sortedKeys.forEach((key, index) => {
        const evaluation = evaluations[key];
        if (index === 0) {
            evaluation.judgement = undefined;
            return;
        }

        const prevEvaluation = evaluations[sortedKeys[index - 1]];
        evaluation.judgement = getLichessJugdement(
            parseFloat(prevEvaluation.evaluation),
            parseFloat(evaluation.evaluation),
            playerColor,
        );
    });
};

export type MaterialDifferencePiece = "p" | "n" | "b" | "r" | "q" | "value";

// Return the taken pieces a color has taken that the other color has not on this format: { w: { p: 2, n: 1, r: 1, b: 1, q: 0, value: 2 }, b: { p: 0, n: 0, r: 0, b: 1, q: 0, value: 0 } }
export const getTakenMaterialDifference = (game: Chess): Record<Color, Record<MaterialDifferencePiece, number>> => {
    const takenPieces: Record<Color, Record<MaterialDifferencePiece, number>> = {
        w: { p: 0, n: 0, r: 0, b: 0, q: 0, value: 0 },
        b: { p: 0, n: 0, r: 0, b: 0, q: 0, value: 0 },
        // w: { p: 2, n: 1, r: 1, b: 1, q: 0, value: 2 },
    };
    const history = game.history({ verbose: true });

    history.forEach((move) => {
        if (move.captured) {
            const color = move.color === "w" ? "b" : "w"; // The color that made the move is the one that captured
            const piece = move.captured as PieceSymbol;
            if (piece === "p") {
                takenPieces[color]["p"] += 1;
                takenPieces[color]["value"] += 1;
            } else if (piece === "n") {
                takenPieces[color]["n"] += 1;
                takenPieces[color]["value"] += 3;
            } else if (piece === "b") {
                takenPieces[color]["b"] += 1;
                takenPieces[color]["value"] += 3;
            } else if (piece === "r") {
                takenPieces[color]["r"] += 1;
                takenPieces[color]["value"] += 5;
            } else if (piece === "q") {
                takenPieces[color]["q"] += 1;
                takenPieces[color]["value"] += 9;
            }
        }
    });

    let takenPiecesDifference: Record<Color, Record<MaterialDifferencePiece, number>> = {
        w: { p: 0, n: 0, r: 0, b: 0, q: 0, value: 0 },
        b: { p: 0, n: 0, r: 0, b: 0, q: 0, value: 0 },
    };

    for (const color in takenPieces) {
        const opponentColor = color === "w" ? "b" : "w";
        for (const piece in takenPieces[color as Color]) {
            takenPiecesDifference[color as Color][piece as MaterialDifferencePiece] = Math.max(
                takenPieces[color as Color][piece as MaterialDifferencePiece] -
                    takenPieces[opponentColor as Color][piece as MaterialDifferencePiece],
                0,
            );
        }
    }

    return takenPiecesDifference;
};
